import type { FormGroup } from '@angular/forms';
import type { ReactiveFormBuilderDiagnosticScope } from '../../adapters/reactive-form-builder.adapters';
import type { Form, FormField } from '../../interfaces/form.interface';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';
import { FormOperations } from '../form-operations';
import { ReactiveFormModelFactory } from '../../model/reactive-form-model.factory';
import {
  ReactiveFormModelPatcher,
  type ReactiveFormPatcherContext,
} from '../../model/reactive-form-model-patcher';
import { ReactiveTwoFieldsConcatConnector } from '../../model/reactive-two-fields-concat.connector';
import { RepeatableBindingsUtils } from '../../model/repeatable-bindings.utils';

export type ReactiveFormModelDiagnosticScope = ReactiveFormBuilderDiagnosticScope;

export interface ReactiveFormModelFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly getPreviousData: () => T | null;
  readonly areExternalDropdownsReady: () => boolean;
  readonly setModel: (model: FormGroup) => void;
  readonly setOriginalModel: (model: Record<string, unknown>) => void;
  readonly startDiagnostics?: (operation: string) => ReactiveFormModelDiagnosticScope | null;
}

/**
 * Owns the reactive model primitives that used to live in the Angular component.
 *
 * The facade keeps model creation, pristine patching, repeatable bindings and FormOperations in one place, so the
 * component can delegate model mutations without also knowing how the legacy builder patches selected values.
 */
export class ReactiveFormModelFacade<T> {
  private readonly modelFactory = new ReactiveFormModelFactory<T>();
  private readonly modelPatcher = new ReactiveFormModelPatcher<T>();
  private readonly twoFieldsConcatConnector = new ReactiveTwoFieldsConcatConnector<T>();

  private formOperations: FormOperations<T> | null = null;
  private model: FormGroup | null = null;
  private repeatableBindingControlNames = new Set<string>();

  constructor(private readonly configuration: ReactiveFormModelFacadeConfiguration<T>) {}

  public getModel(): FormGroup | null {
    return this.model;
  }

  public hasFormOperations(): boolean {
    return this.formOperations !== null;
  }

  public initializeModel(): void {
    const form = this.requireForm();
    const scope = this.configuration.startDiagnostics?.('initialize-model') ?? null;

    try {
      const model = this.modelFactory.create({
        form,
        previousData: this.configuration.getPreviousData(),
        externalDropdownsReady: this.configuration.areExternalDropdownsReady(),
      });

      this.model = model;
      this.configuration.setModel(model);
      this.twoFieldsConcatConnector.bind(form, model);
      this.formOperations = new FormOperations(form, model);
      this.applyInitialSelectedValues();
      scope?.addMetric?.('controls', Object.keys(model.controls).length, 'items');
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  public rebuildFormOperations(): void {
    const form = this.configuration.getForm();
    const model = this.model;

    if (!form || !model) {
      this.formOperations = null;
      return;
    }

    this.formOperations = new FormOperations(form, model);
    this.indexRepeatableBindingControls();
  }

  public indexRepeatableBindingControls(): void {
    this.repeatableBindingControlNames = RepeatableBindingsUtils.index(this.requireForm());
  }

  public isRepeatableBindingControlName(fieldName: string): boolean {
    return this.repeatableBindingControlNames.has(fieldName);
  }

  public applySelected(field: FormField<T> | undefined, value: T | null): void {
    this.requireFormOperations().applySelected(field, value);
  }

  public getFieldByControlName(fieldName: string): FormField<T> | undefined {
    return this.requireFormOperations().getFieldByControlName(fieldName);
  }

  public async executeFieldsLogic(fieldName: string | null, origin: ReactiveChangeOrigin): Promise<void> {
    await this.requireFormOperations().executeFieldsLogic(fieldName, origin);
  }

  public patchFromPreviousDataPristine(): void {
    this.runWithDiagnostics('patch-from-previous-data-pristine', () => {
      this.modelPatcher.patchFromPreviousDataPristine(this.getPatcherContext());
    });
  }

  public remapDropdownFieldsPristine(): void {
    this.runWithDiagnostics('remap-dropdown-fields-pristine', () => {
      this.modelPatcher.remapDropdownFieldsPristine(this.getPatcherContext());
    });
  }

  public remapDropdownFieldsPristineByName(fieldNames: readonly string[]): void {
    this.runWithDiagnostics('remap-dropdown-fields-pristine-scoped', () => {
      this.modelPatcher.remapDropdownFieldsPristineByName(this.getPatcherContext(), fieldNames);
    });
  }

  public applyInitialSelectedValues(): void {
    this.modelPatcher.applyInitialSelectedValues(this.getPatcherContext());
  }

  public patchRepeatableBindingsFromPreviousDataPristine(): void {
    this.runWithDiagnostics('patch-repeatable-bindings', () => {
      RepeatableBindingsUtils.patchFromPreviousDataPristine(
        this.requireForm(),
        this.requireModel(),
        this.configuration.getPreviousData(),
      );
    });
  }

  public setOriginalModelFromCurrent(): void {
    const model = { ...(this.requireModel().getRawValue() as Record<string, unknown>) };

    this.configuration.setOriginalModel(model);
  }

  public destroy(): void {
    this.twoFieldsConcatConnector.destroy();
  }

  private runWithDiagnostics(operation: string, callback: () => void): void {
    const scope = this.configuration.startDiagnostics?.(operation) ?? null;

    try {
      callback();
      scope?.end();
    } catch (error: unknown) {
      scope?.fail(error);
      throw error;
    }
  }

  private getPatcherContext(): ReactiveFormPatcherContext<T> {
    return {
      form: this.requireForm(),
      model: this.requireModel(),
      previousData: this.configuration.getPreviousData(),
      externalDropdownsReady: this.configuration.areExternalDropdownsReady(),
      formOperations: this.requireFormOperations(),
    };
  }

  private requireForm(): Form<T> {
    const form = this.configuration.getForm();

    if (!form) {
      throw new Error('Reactive form definition is not available.');
    }

    return form;
  }

  private requireModel(): FormGroup {
    if (!this.model) {
      throw new Error('Reactive form model is not initialized.');
    }

    return this.model;
  }

  private requireFormOperations(): FormOperations<T> {
    if (!this.formOperations) {
      throw new Error('Reactive form operations are not initialized.');
    }

    return this.formOperations;
  }
}
